import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ApiClient } from '../api/client';
import type { AppItem } from '../types';
import ConnectionSecrets from '../components/ConnectionSecrets';
import '../styles/connections.css';
import './ConnectionsSettingsPage.css';

export default function ConnectionsSettingsPage({ apiClient }: { apiClient: ApiClient }) {
  const [apps, setApps] = useState<AppItem[] | null>(null);
  const [identity, setIdentity] = useState<{ configured: boolean; enabled: boolean; auth_mode?: string | null } | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  useEffect(() => {
    let active = true;
    void apiClient.get<AppItem[]>('/api/apps')
      .then(value => { if (active) setApps(value); })
      .catch(() => { if (active) setErrors(values => [...values, 'Could not load applications.']); });
    void apiClient.get<typeof identity>('/api/settings/yml-id')
      .then(value => { if (active) setIdentity(value); })
      .catch(() => { if (active) setErrors(values => [...values, 'Could not load identity provisioning status.']); });
    return () => { active = false; };
  }, [apiClient]);
  const controlPlane = apps?.find(app => app.is_self);
  const identityUnavailable = errors.includes('Could not load identity provisioning status.');
  return <div className="connection-settings">
    <header className="connection-intro">
      <Link className="connection-back" to="/settings"><span aria-hidden="true">←</span> Back to settings</Link>
      <h2>Connections &amp; secrets</h2>
      <p>Set up providers once. Manage the credentials your applications share, all in one place.</p>
    </header>
    {errors.map(error => <p className="connection-notice connection-notice--error" key={error} role="alert">{error} Reload to try again.</p>)}
    <div className="connection-providers">
    <section className="card connection-provider">
      <div className="connection-provider-heading"><h3>Codex Cloud</h3><span className="connection-status">Private · Experimental</span></div>
      <p>Connect your trusted owner-side controller and continue hosted conversations using your ChatGPT subscription.</p>
      <div className="connection-provider-actions"><Link className="button secondary" to="/cloud">Manage Cloud conversations <span aria-hidden="true">→</span></Link></div>
    </section>
    <section className="card connection-provider">
      <div className="connection-provider-heading"><h3>Google Drive</h3><span className="connection-status">Shared registration</span></div>
      <p>One Google registration for all your apps. Each owner chooses which apps can access their private folders.</p>
      <div className="connection-provider-actions"><Link className="button secondary" to="/connections/google">Manage Google Drive <span aria-hidden="true">→</span></Link></div>
    </section>
    <section className="card connection-provider">
      <div className="connection-provider-heading"><h3>Identity provisioning</h3><span className={`connection-status${identity?.configured && identity.enabled ? ' connection-status--success' : ''}`}>{identity ? identity.configured ? identity.enabled ? 'Enabled' : 'Disabled' : 'Not configured' : identityUnavailable ? 'Unavailable' : 'Loading…'}</span></div>
      <p>A management client creates separate credentials for each app and stage. Update its credentials in Mission Control’s application secrets below.</p>
      {identity?.configured && <p className="connection-help">{identity.auth_mode === 'client_credentials' ? 'Machine client' : 'Service account'} · Client-management access required</p>}
      {controlPlane && <Link className="connection-text-link" to={`/apps/${encodeURIComponent(controlPlane.id)}`}>Mission Control application settings</Link>}
      <div className="connection-provider-actions">
        <Link className="button secondary" to="/settings#settings-home-36-access">Test provider access <span aria-hidden="true">→</span></Link>
      </div>
    </section>
    <section className="card connection-provider">
      <div className="connection-provider-heading"><h3>AI connections</h3><span className="connection-status">Workloads unavailable</span></div>
      <p>Manage provider credentials at the AI gateway. Apps use their own identities; saving credentials does not enable AI processing.</p>
      <div className="connection-provider-actions"><Link className="button secondary" to="/settings/models">Model authentication <span aria-hidden="true">→</span></Link></div>
    </section>
    </div>
    {apps ? <ConnectionSecrets apiClient={apiClient} apps={apps} /> : <p role="status">Loading applications…</p>}
  </div>;
}
